import { BillStatus } from "@redotech/crdb-enums/billing/bill-status";
import { BillType } from "@redotech/crdb-enums/billing/bill-type";
import { SubscriptionProduct } from "@redotech/redo-billing-common/unified-billing/enums";
import { AdvancedFilterType } from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import type { AdvancedTableFilter } from "@redotech/redo-web/advanced-filters/advanced-filter";
import { encodeAdvancedFiltersData } from "@redotech/redo-web/advanced-filters/advanced-filter";
import {
  INVOICE_FILTER_NAME_PRODUCT,
  INVOICE_FILTER_NAME_STATUS,
  INVOICE_FILTER_NAME_TYPE,
  productFilter,
  statusFilter,
} from "./invoice-filter-definitions";

/** Chip filters, encoded the same way every other view encodes them. */
export const BILL_FILTERS_PARAM = "filters";
export const BILL_SEARCH_PARAM = "search";

export interface BillFilterParams {
  statuses?: BillStatus[];
  products?: SubscriptionProduct[];
  types?: BillType[];
}

/**
 * Push the invoice table's chip filters into getBills so filtering happens in
 * CRDB. Billed date isn't among them — the summary card's picker scopes that.
 */
export function filtersToBillParams(
  filters: AdvancedTableFilter[],
): BillFilterParams {
  const params: BillFilterParams = {};

  for (const filter of filters) {
    if (filter.type !== AdvancedFilterType.ARRAY_TO_STRING) {
      continue;
    }
    const selected = filter.data.value;
    if (!selected?.length) {
      continue;
    }
    switch (filter.data.name) {
      case INVOICE_FILTER_NAME_STATUS: {
        params.statuses = Object.values(BillStatus).filter((s) =>
          selected.includes(s),
        );

        break;
      }
      case INVOICE_FILTER_NAME_PRODUCT: {
        params.products = Object.values(SubscriptionProduct).filter((p) =>
          selected.includes(p),
        );

        break;
      }
      case INVOICE_FILTER_NAME_TYPE: {
        params.types = Object.values(BillType).filter((t) =>
          selected.includes(t),
        );

        break;
      }
      // No default
    }
  }
  return params;
}

/**
 * The `filters` param value that lands on Bills with chips already applied.
 * Empty selections are omitted so a link never carries an unscoped chip.
 */
export function encodeBillFilters({
  products = [],
  statuses = [],
}: BillFilterParams): string {
  return encodeAdvancedFiltersData([
    ...(statuses.length
      ? [{ ...statusFilter.data, value: [...statuses] }]
      : []),
    ...(products.length
      ? [{ ...productFilter.data, value: [...products] }]
      : []),
  ]);
}
